import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Material from "@mui/material";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import EditIcon from "@mui/icons-material/Edit";
import "../../styles/uploadPhotoStyles.scss";
import ImageCropModal from "../ImageCropModal";
import { useUploadCommunityAsset } from "../../hooks/useUploadCommunityAsset";

async function dataUrlToFile(dataUrl, filename) {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

/**
 * Image picker + optional crop modal + presigned S3 upload for a community asset category.
 *
 * @param {{
 *   communityId: number|string|null|undefined,
 *   category: string,
 *   value?: string | null,
 *   onChange: (finalUrl: string) => void,
 *   onUploadingChange?: (uploading: boolean) => void,
 *   aspectRatio?: number,
 *   size?: number,
 *   imageMaxWidth?: number,
 *   title?: string,
 *   displayTitle?: boolean,
 *   rounded?: boolean,
 *   className?: string,
 *   variant?: string,
 *   showEditOnHover?: boolean,
 * }} props
 */
export default function CommunityAssetImageField({
  communityId,
  category,
  value,
  onChange,
  onUploadingChange,
  aspectRatio = 1,
  size = 160,
  fullWidth = false,
  imageMaxWidth = 720,
  title,
  displayTitle = false,
  rounded = false,
  className = "",
  variant = "light",
  showEditOnHover = true,
}) {
  const { t } = useTranslation("content");
  const fieldTitle = title ?? t("upload.imageTitle");
  const inputRef = useRef();
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropSource, setCropSource] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showGlass, setShowGlass] = useState(false);
  const [glassLeaving, setGlassLeaving] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const { uploadFile } = useUploadCommunityAsset({
    communityId,
    onUploadingChange,
  });

  useEffect(() => {
    if (value && value.startsWith("http")) {
      setLocalPreview(null);
    }
  }, [value]);

  useEffect(() => {
    if (uploading) {
      setShowGlass(true);
      setGlassLeaving(false);
      return;
    }
    if (showGlass) {
      setGlassLeaving(true);
      const id = window.setTimeout(() => {
        setShowGlass(false);
        setGlassLeaving(false);
      }, 380);
      return () => window.clearTimeout(id);
    }
  }, [uploading, showGlass]);

  useEffect(() => {
    if (!uploadSuccess) return undefined;
    const id = window.setTimeout(() => setUploadSuccess(false), 2800);
    return () => window.clearTimeout(id);
  }, [uploadSuccess]);

  const displaySrc = localPreview || (value?.startsWith?.("http") ? value : null);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = null;
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setCropSource(e.target.result);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = async (dataUrl) => {
    setLocalPreview(dataUrl);
    setUploading(true);
    try {
      const file = await dataUrlToFile(dataUrl, "upload.jpg");
      const url = await uploadFile(file, category);
      onChange(url);
      // Keep localPreview showing the just-cropped image; the
      // useEffect([value]) below clears it once the parent propagates
      // the new URL. Clearing here would blank the field whenever the
      // parent stores `value` in a ref and doesn't re-render (e.g.
      // when its hasUpdates flag is already true).
      setUploadSuccess(true);
    } catch (e) {
      console.error(e);
      window.setSnackbar?.(t("upload.imageError"));
      setLocalPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const openPicker = (e) => {
    e?.stopPropagation?.();
    inputRef.current?.click();
  };

  return (
    <>
      <div
        className={
          "upload-asset-image-field upload-photo sh-1 " +
          variant +
          " " +
          (rounded ? "rounded-upload" : "") +
          " " +
          className +
          (uploading ? " upload-asset-image-field--uploading" : "") +
          (showGlass ? " upload-asset-image-field--glass" : "") +
          (glassLeaving ? " upload-asset-image-field--glass-leaving" : "") +
          (uploading || showGlass ? " upload-asset-image-field--busy" : "")
        }
        style={{
          height: (fullWidth || !size) ? "auto" : `${size}px`,
          width: (fullWidth || !size) ? "100%" : `${size * aspectRatio}px`,
          minWidth: (fullWidth || !size) ? "100%" : `${size * aspectRatio}px`,
          aspectRatio: (fullWidth || !size) ? aspectRatio : undefined,
        }}
        onClick={openPicker}
      >
        {displaySrc && (
          <div
            className={
              "upload-asset-image-field__media" +
              (rounded ? " upload-asset-image-field__media--rounded" : "")
            }
          >
            <img
              src={displaySrc}
              alt=""
              className={uploadSuccess ? "upload-asset-image-field__img--glow" : ""}
              style={{
                borderRadius: rounded ? "50%" : "9px",
              }}
            />
            {showGlass && (
              <div
                className="upload-asset-image-field__glass"
                aria-hidden
              >
                <span className="upload-asset-image-field__glass-shimmer" />
              </div>
            )}
          </div>
        )}

        {showEditOnHover && displaySrc && !uploading && !showGlass && (
          <div className="upload-asset-image-field__overlay">
            <Material.IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openPicker(e);
              }}
              sx={{ color: "common.white" }}
            >
              <EditIcon fontSize="small" />
            </Material.IconButton>
          </div>
        )}

        <div className={" " + (!displaySrc && "empty")}>
          <AddAPhotoIcon
            fontSize="small"
            className="add-photo-icon"
            color="black"
          />
          {displayTitle && (
            <span className="add-photo-title">{fieldTitle}</span>
          )}
        </div>

        <input
          accept="image/png, image/jpeg, image/jpg"
          ref={inputRef}
          style={{ visibility: "hidden", height: 0, width: 0 }}
          type="file"
          onChange={handleFileChange}
        />
      </div>

      <ImageCropModal
        open={cropModalOpen && !!cropSource}
        onClose={() => {
          setCropModalOpen(false);
          setCropSource(null);
        }}
        title={fieldTitle}
        aspectRatio={aspectRatio}
        imageMaxWidth={imageMaxWidth}
        imageDataUrl={cropSource}
        onConfirm={handleCropConfirm}
      />
    </>
  );
}
